import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from '../primitives';
import { useAuth } from '../../features/auth/AuthContext';
import { DeleteAccountModal } from '../../features/auth/DeleteAccountModal';
import { AccountSettingsModal } from '../../features/ucet/AccountSettingsModal';
import { cx } from '../../lib/cx';
import styles from './UserMenu.module.css';

const MENU_W = 240;

/**
 * Avatar přihlášeného uživatele v hlavičce. Po kliknutí rozbalí podokno se
 * jménem/e-mailem a tlačítkem „Odhlásit se". Hlavní cesta k odhlášení v PWA na
 * mobilu, kde je patička sidebaru (s tlačítkem Odhlásit) skrytá.
 *
 * Menu se renderuje přes portal do <body> s `position: fixed` — hlavička má pod
 * 1100px `overflow: hidden` (kvůli zoom-out fixu na mobilu), takže dropdown
 * uvnitř by se oříznul.
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'deactivate' | 'delete' | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = user?.full_name || user?.email || 'Nepřihlášen';
  const initials = (user?.full_name || user?.email || '??').slice(0, 2).toUpperCase();
  const role = user?.is_admin ? 'admin' : user?.can_zapis_kd ? 'stavbyvedoucí' : 'uživatel';

  // Umísti menu vůči avataru; přepočítej při scrollu/resize.
  useLayoutEffect(() => {
    if (!open) return;
    function place() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      let left = r.right - MENU_W; // zarovnáno na pravý okraj avataru
      left = Math.max(8, Math.min(left, window.innerWidth - 8 - MENU_W));
      setPos({ top: r.bottom + 8, left });
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Zavření kliknutím mimo / Esc.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Účet uživatele"
        title={displayName}
      >
        <Avatar initials={initials} size="sm" current />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label="Účet"
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              width: MENU_W,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <div className={styles.header}>
              <Avatar initials={initials} size="md" current />
              <div className={styles.meta}>
                <span className={styles.name}>{displayName}</span>
                {user?.email && user?.full_name && <span className={styles.email}>{user.email}</span>}
                <span className={styles.role}>{role}</span>
              </div>
            </div>

            <div className={styles.divider} />

            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                setOpen(false);
                setSettingsOpen(true);
              }}
            >
              <span className={styles.itemIcon} aria-hidden>
                ⚙
              </span>
              Nastavení účtu
            </button>

            <button
              type="button"
              role="menuitem"
              className={cx(styles.item, styles.logout)}
              onClick={() => {
                setOpen(false);
                logout();
              }}
            >
              <span className={styles.itemIcon} aria-hidden>
                ⏻
              </span>
              Odhlásit se
            </button>

            {/* Záměrně malé a nenápadné, ať se na ně neklikne omylem — obojí se ještě potvrzuje heslem. */}
            <div className={styles.dangerZone}>
              <button
                type="button"
                className={styles.dangerLink}
                onClick={() => {
                  setOpen(false);
                  setDeleteMode('deactivate');
                }}
              >
                Deaktivovat účet
              </button>
              <span aria-hidden>·</span>
              <button
                type="button"
                className={styles.dangerLink}
                onClick={() => {
                  setOpen(false);
                  setDeleteMode('delete');
                }}
              >
                Smazat účet
              </button>
            </div>
          </div>,
          document.body,
        )}

      <DeleteAccountModal
        open={deleteMode !== null}
        initialMode={deleteMode ?? 'deactivate'}
        onClose={() => setDeleteMode(null)}
      />
      <AccountSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
